// Timezone-safe calendar-date formatting.
//
// `date.toISOString().split('T')[0]` converts to UTC first, so a Date built
// at LOCAL midnight (e.g. meal-plan grid days, setHours(0,0,0,0)) lands on
// the PREVIOUS calendar day anywhere east of Greenwich (Perth is UTC+8:
// Mon 00:00 local == Sun 16:00 UTC). These helpers format the date the user
// actually sees, using local components. Always use these - never
// toISOString() - when the value is a calendar date rather than a moment.

export const toLocalYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const toLocalYM = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// ---------------------------------------------------------------------------
// Parsing dates that came from the API (v15.01)
// ---------------------------------------------------------------------------
//
// The backend serialises timestamps with Python's datetime.isoformat(), which
// emits MICROSECONDS - "2026-07-23T01:47:06.619425+00:00". V8 (Chrome, Node)
// parses that happily. Safari / iOS WebKit does NOT: the ECMAScript date-time
// format allows at most 3 fractional digits, and WebKit enforces it. So on
// Ivan's iPhone every one of these strings became `Invalid Date`, and because
// `new Date(bad).toLocaleDateString()` RETURNS the string "Invalid Date"
// rather than throwing, every try/catch around it passed and the words
// "Invalid Date" were rendered straight into the UI.
//
// parseApiDate normalises the string before handing it to Date:
//   * trims fractional seconds to 3 digits (the microseconds case)
//   * turns a space separator into "T" (Safari rejects "2026-07-23 01:47:06")
// and returns null - never an Invalid Date - when there's nothing usable.

export function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (!raw) return null;

  // A DATE-ONLY string is a calendar date, not a moment. `new Date("2026-09-22")`
  // is parsed as UTC midnight by spec, so everywhere west of Greenwich it
  // renders as the 21st - the same off-by-one this file's first half exists to
  // prevent, arriving from the other direction. Build it from local parts
  // instead, so the day a person sees is the day the server named. (v18.33)
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    return isNaN(d.getTime()) ? null : d;
  }

  const normalised = raw
    .replace(' ', 'T')                    // "…-23 01:47" -> "…-23T01:47"
    .replace(/(\.\d{3})\d+/, '$1');       // microseconds -> milliseconds

  let d = new Date(normalised);
  if (!isNaN(d.getTime())) return d;
  // Fall back to the untouched string in case normalising broke a format the
  // engine would otherwise have accepted.
  d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

// ---------------------------------------------------------------------------
// WHOSE LANGUAGE (v18.33)
// ---------------------------------------------------------------------------
//
// `locale` was optional and every one of the nine call sites left it out, so
// the argument fell through to
//
//     toLocaleDateString(undefined, ...)
//
// and `undefined` means THE RUNTIME'S locale - the phone's - not the language
// the household picked in Settings. A family on an English phone who set the
// app to Spanish got "Sep 22" on the Lists page, in the cost split, in the
// price history and on a shared list, while every word around it was Spanish.
//
// It is the same mistake the analytics month names had, and it was found the
// same afternoon, by the same render: the first version of monthLabel.js
// copied this call because this was the existing code that looked correct.
//
// The language now comes from the app by default, with the reader's REGION
// attached where the device offers one - see readerLocale in src/i18n. A bare
// "en" is American, so v18.33's fix to the WORDS reversed the NUMBERS until
// v18.35 put the region back.
//
// No caller passes a locale any more either. The calendar used to pass the
// language it held, which looked like the careful thing to do and was the
// same bug in its most convincing form. One place decides.

import { readerLocale } from "@/i18n";

/** Localised date string, or '' when the value is missing/unparseable. */
export function formatApiDate(value, options) {
  const d = parseApiDate(value);
  if (!d) return '';
  try { return d.toLocaleDateString(readerLocale(), options); }
  catch { return ''; }
}

/**
 * The same, for a time of day - the reminders list shows "7:30 am" under its
 * date line. It had the same `[]` first argument, which is the device's
 * locale, so a Spanish household on an English phone read a 12-hour clock
 * where their language uses 24. (v18.33)
 *
 * NOTE THE MISSING THIRD PARAMETER, here and above. Both took an optional
 * `locale` until v18.35. An optional locale is an invitation to judge, at the
 * call site, which locale is the right one - and that judgement is the thing
 * that failed: nine callers omitted it and got the phone's, five callers in
 * the fork supplied the language they held and got America. There is no
 * caller-knows-better case, so there is no longer an argument for one.
 */
export function formatApiTime(value, options) {
  const d = parseApiDate(value);
  if (!d) return '';
  try { return d.toLocaleTimeString(readerLocale(), options); }
  catch { return ''; }
}
